import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type { PipelineLoan } from '../types';
import { REPORTABLE_FOLDER_FILTER, isReportableLoan } from './reportEvents';
import {
  attachCanonicalPayoutDates,
  type PayoutConflictWarning,
  type ReportPayoutRow
} from './payoutMatching';

const log = createComponentLogger('CanonicalReportData', 'reports');

export const DEFAULT_REPORT_PAGE_SIZE = 1000;

export const REPORTING_LOAN_SELECT = `
  organization_id,
  deleted_from_encompass,
  encompass_loan_id,
  trans_details_loan_number,
  borrower_first_name,
  borrower_last_name,
  loan_officer_first_name,
  loan_officer_last_name,
  trans_details_loan_amt,
  subject_property_state,
  rate_lock_buy_side_lock_date,
  rate_lock_sell_side_lock_date,
  rate_lock_request_lock_date,
  rate_lock_buy_side_lock_expires,
  rate_lock_status,
  trans_details_interest_rate,
  rate_lock_loan_program,
  rate_lock_investor_commitment,
  investor_name,
  milestone,
  milestone_history,
  last_conversation_log_at,
  milestone_date,
  shipping_investor_delivery_date,
  loan_folder,
  channel,
  creation_date,
  application_date,
  closing_date,
  funded_date,
  purchased_date,
  cx_ls_date_processing,
  cx_ls_date_uw,
  cd_sent_date,
  loan_processor_first_name,
  loan_processor_last_name,
  underwriter_first_name,
  underwriter_last_name,
  closer_first_name,
  closer_last_name,
  loan_officer_assistant_first_name,
  loan_officer_assistant_last_name,
  loan_officer_2_first_name,
  loan_officer_2_last_name,
  freddie_mac_ltv_ratio_pct,
  trans_details_cltv_ratio_pct,
  lien_position,
  trans_details_loan_purpose,
  trans_details_loan_type,
  amortization_type,
  cx_doc_type,
  rate_lock_documentation_type,
  dscr_ratio
`;

const REPORTING_PAYOUT_SELECT = 'id, organization_id, encompass_loan_id, loan_number, fund_date';

export interface CanonicalReportLoan extends PipelineLoan {
  organization_id: string | null;
  deleted_from_encompass: boolean | null;
}

export type ReportingDataSource = 'loans' | 'payouts';

export class ReportingDataIssue extends Error {
  readonly source: ReportingDataSource;
  readonly cause: unknown;

  constructor(source: ReportingDataSource, cause: unknown) {
    const sourceLabel = source === 'loans' ? 'loan pipeline' : 'broker payout dates';
    super(`Unable to load ${sourceLabel}.`);
    this.name = 'ReportingDataIssue';
    this.source = source;
    this.cause = cause;
  }
}

export interface CanonicalReportDataResult {
  loans: CanonicalReportLoan[];
  payouts: ReportPayoutRow[];
  payoutWarnings: PayoutConflictWarning[];
  loanError: ReportingDataIssue | null;
  payoutError: ReportingDataIssue | null;
  isPartial: boolean;
}

export interface PageResult<T> {
  data: T[] | null;
  error: unknown;
}

export interface PagedFetchResult<T> {
  data: T[];
  error: unknown | null;
}

export async function fetchAllPages<T>(
  loadPage: (from: number, to: number) => Promise<PageResult<T>>,
  pageSize = DEFAULT_REPORT_PAGE_SIZE
): Promise<PagedFetchResult<T>> {
  const allRows: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const result = await loadPage(from, from + pageSize - 1);
    if (result.error) {
      return { data: allRows, error: result.error };
    }
    const rows = result.data || [];
    allRows.push(...rows);
    if (rows.length < pageSize) {
      return { data: allRows, error: null };
    }
  }
}

type ReportingClient = Pick<typeof supabase, 'from'>;

export interface FetchCanonicalReportDataOptions {
  pageSize?: number;
}

/**
 * Load the all-valid-loans historical reporting population and payout dates.
 * A loan query error is blocking. A payout query error is returned explicitly
 * with otherwise usable loan data so consumers cannot silently report zeros.
 */
export async function fetchCanonicalReportData(
  client: ReportingClient = supabase,
  options: FetchCanonicalReportDataOptions = {}
): Promise<CanonicalReportDataResult> {
  const pageSize = options.pageSize || DEFAULT_REPORT_PAGE_SIZE;
  const loanResult = await fetchAllPages<CanonicalReportLoan>(
    async (from, to) => {
      const result = await client
        .from('encompass_loans')
        .select(REPORTING_LOAN_SELECT)
        .not('deleted_from_encompass', 'is', true)
        .or(REPORTABLE_FOLDER_FILTER)
        .order('encompass_loan_id', { ascending: true })
        .range(from, to);
      return {
        data: (result.data || []) as CanonicalReportLoan[],
        error: result.error
      };
    },
    pageSize
  );

  if (loanResult.error) {
    const loanError = new ReportingDataIssue('loans', loanResult.error);
    log.error('Failed to load canonical reporting loans', { error: loanResult.error });
    return {
      loans: [],
      payouts: [],
      payoutWarnings: [],
      loanError,
      payoutError: null,
      isPartial: false
    };
  }

  const reportableLoans = loanResult.data.filter(isReportableLoan);
  const payoutResult = await fetchAllPages<ReportPayoutRow>(
    async (from, to) => {
      const result = await client
        .from('payouts')
        .select(REPORTING_PAYOUT_SELECT)
        .not('fund_date', 'is', null)
        .order('fund_date', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to);
      return {
        data: (result.data || []) as ReportPayoutRow[],
        error: result.error
      };
    },
    pageSize
  );

  if (payoutResult.error) {
    const payoutError = new ReportingDataIssue('payouts', payoutResult.error);
    log.warn('Failed to load canonical payout dates', { error: payoutResult.error });
    return {
      loans: reportableLoans.map((loan) => ({ ...loan, broker_fund_date: null })),
      payouts: [],
      payoutWarnings: [],
      loanError: null,
      payoutError,
      isPartial: true
    };
  }

  const matched = attachCanonicalPayoutDates(reportableLoans, payoutResult.data);
  if (matched.warnings.length > 0) {
    log.warn('Conflicting payout fund dates resolved to earliest date', {
      count: matched.warnings.length,
      warnings: matched.warnings
    });
  }

  return {
    loans: matched.loans,
    payouts: payoutResult.data,
    payoutWarnings: matched.warnings,
    loanError: null,
    payoutError: null,
    isPartial: false
  };
}
